import { useState } from 'react'
import { clsx } from 'clsx'
import Button from './Button'
import Icon from '@/components/ui/Icon'
import styles from './ReminderPicker.module.css'
import type { RepeatConfig } from '@/services/api'

interface ReminderPickerProps {
  onSave: (scheduledAt: Date, repeatConfig?: RepeatConfig) => void
  onClose: () => void
  onCustomClick?: () => void
  className?: string
}

interface QuickPickOption {
  label: string
  getDate: () => Date
}

const getQuickPickOptions = (): QuickPickOption[] => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  return [
    {
      label: 'In 1 hour',
      getDate: () => new Date(now.getTime() + 60 * 60 * 1000),
    },
    {
      label: 'This evening',
      getDate: () => {
        const evening = new Date(today)
        evening.setHours(18, 0, 0, 0)
        // If already past 6pm, set for tomorrow
        if (evening <= now) {
          evening.setDate(evening.getDate() + 1)
        }
        return evening
      },
    },
    {
      label: 'Tomorrow morning',
      getDate: () => {
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)
        tomorrow.setHours(9, 0, 0, 0)
        return tomorrow
      },
    },
    {
      label: 'Tomorrow evening',
      getDate: () => {
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)
        tomorrow.setHours(18, 0, 0, 0)
        return tomorrow
      },
    },
  ]
}

export default function ReminderPicker({
  onSave,
  onClose,
  onCustomClick,
  className,
}: ReminderPickerProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const quickPickOptions = getQuickPickOptions()

  const handleQuickPick = async (option: QuickPickOption) => {
    setSelectedOption(option.label)
    setIsLoading(true)

    try {
      const scheduledAt = option.getDate()
      await onSave(scheduledAt, { type: 'once' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCustomClick = () => {
    if (onCustomClick) {
      onCustomClick()
    }
  }

  return (
    <div className={clsx(styles.picker, className)}>
      <div className={styles.header}>
        <h3 className={styles.title}>Set Reminder</h3>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className={styles.options}>
        {quickPickOptions.map((option) => (
          <button
            key={option.label}
            className={clsx(
              styles.optionButton,
              selectedOption === option.label && styles.selected
            )}
            onClick={() => handleQuickPick(option)}
            disabled={isLoading}
          >
            <span className={styles.optionIcon}><Icon name="clock" size={20} /></span>
            <span className={styles.optionLabel}>{option.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.divider}>
        <span>or</span>
      </div>

      <Button
        variant="outline"
        fullWidth
        onClick={handleCustomClick}
        disabled={isLoading}
      >
        Custom date & time
      </Button>
    </div>
  )
}
